import {ValidatorModel} from "../common/models/validator";
import i18n from "../i18n/i18n";
import {ValidatedValue} from "./validatedValue";
import validator from "validator";
export default class Validator {
    public static validate(value: any, validators: ValidatorModel[]) {
        let errorMessage = "";

        validators.forEach((validator) => {
            if (errorMessage !== "") {
                return errorMessage;
            }

            const isValid = validator.fn(value);
            if (!isValid) {
                errorMessage = validator.errorMessage();
                return errorMessage;
            }
        });

        return errorMessage;
    }

    public static isNotEmpty(value: string | number): boolean {
        if (!value) {
            return false;
        }
        return !!value.toString().trim();
    }

    public static isCorrectPassword(value: string): boolean {
        if (!value) {
            return false;
        }

        const valid = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/;
        return value ? valid.test(value) : true;
    }

    // Checking if the date is in the correct format
    // for pl language: dd.mm.yyyy
    // for en language: mm/dd/yyyy
    public static isCorrectDate(value: string): boolean {
        if (!value) {
            return false;
        }
        const dotValid = /[.]/;
        const slashValid = /[/]/;

        let month;
        let day;
        let year;
        let leapYear = false;

        if (dotValid.test(value)) { // polish language, format dd.mm.yyyy
            const tmp = value.split(".");

            day = Number.parseInt(tmp[0]);
            month = Number.parseInt(tmp[1]);
            year = Number.parseInt(tmp[2]);
        } else if (slashValid.test(value)) { // english language, format mm/dd/yyyy
            const tmp = value.split("/");

            day = Number.parseInt(tmp[1]);
            month = Number.parseInt(tmp[0]);
            year = Number.parseInt(tmp[2]);
        } else {
            const date = new Date(value);

            day = date.getDate();
            month = date.getMonth() + 1;
            year = date.getFullYear();
        }

        //month
        if (month > 12 || month < 1) {
            return false;
        }
        // days
        if (day > 31 || day < 1) return false;

        // April, June, September, November
        if (month === 4 || month === 6 || month === 9 || month === 11) {
            if (day > 30 || day < 1) return false;
        }

        // leap year
        if (((year % 4 == 0) && (year % 100 != 0)) || (year % 400 == 0)) leapYear = true;

        if (leapYear) {
            if (month === 2 && day > 29) return false;
        } else if (month === 2 && day > 28) return false;


        return true;
    }

    public static isCorrectDateWithTime(value: Date): boolean {
        if (!value || value.toString() === "Invalid Date") {
            return false;
        }

        return true;
    }

    // Checking if the date consists of numbers,
    // doesn't contains letters or other characters (except . and /)
    public static isDateIsNumber(value: string | Date): boolean {
        if (!value) {
            return false;
        }

        const valid = /^[0-3]?[0-9].[0-3]?[0-9].(?:[0-9]{2})?[0-9]{2}$/;
        const dotValid = /[.]/;
        const slashValid = /[/]/;
        let dateString = "";

        if (typeof value === "object") {
            dateString = value.toLocaleDateString();
        } else if (typeof value === "string") {
            if (dotValid.test(value) || slashValid.test(value)) {
                dateString = value;
            }
        } else return false;

        return value ? valid.test(dateString) : true;
    }

    public static isCorrectTime(value: Date): boolean {
        if (!value || value.toString() === "Invalid Date") {
            return false;
        }

        return true;
    }

    public static isCorrectAddressUrl(value: string): boolean {
        if (!value) {
            return false;
        }

        return validator.isURL(value);
    }

    public static isCorrectAddressUrlWithTCP(value: string): boolean {
        if (!value) {
            return false;
        }

        const domainName = "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}";
        const ipAddress = "((\\d{1,3}\\.){3}\\d{1,3}))";
        const port = ":[0-9]{1,4}?";

        const domainNameOrIpAddressWithoutPort = `^${domainName}|${ipAddress}$`;
        const domainNameOrIpAddressWithPort = `^${domainName}|${ipAddress}${port}$`;

        const valid = new RegExp(`${domainNameOrIpAddressWithoutPort}|${domainNameOrIpAddressWithPort}`, "i");

        return valid.test(value);
    }

    public static minLength(value: string, length: number): boolean {
        if (!value) {
            return false;
        }

        return value.length >= length;
    }

    public static maxLength(value: string, length: number): boolean {
        if (value === null || value === undefined) {
            return false;
        }

        return value.length <= length;
    }

    public static dateFromIsEarlierThanDateTo(dateFrom: Date | null, dateTo: Date | null): boolean {
        if (!dateFrom || !dateTo) {
            return false;
        }

        return new Date(dateFrom).setHours(0, 0, 0, 0) <= new Date(dateTo).setHours(0, 0, 0, 0);
    }

    public static dateWithTimeFromIsEarlierThanDateWithTimeTo(dateFrom: Date | null, dateTo: Date | null): boolean {
        if (!dateFrom || !dateTo) {
            return false;
        }

        return new Date(dateFrom) <= new Date(dateTo);
    }

    public static isOnlyPositiveNumberWithCommaOrEmpty(value: string): boolean {
        if (!value) {
            return true;
        }

        return Validator.isCorrectNumberWithCommaOrEmpty(value) && Math.sign(parseInt(value, 10)) >= 0;
    }

    public static addValidatorsToDateTimePicker(from: ValidatedValue<Date | null>, to: ValidatedValue<Date | null>) {
        from.removeValidators();
        from.clearValidation();

        to.removeValidators();
        to.clearValidation();

        const dateWithTimeValidators = {
            name: "correctDateWithTime",
            fn: this.isCorrectDateWithTime,
            errorMessage: () => i18n.t("reports.errors.correctDateWithTime")
        };

        if (from && from.value)  {
            from.addValidators([dateWithTimeValidators]);
        }

        if (to && to.value)  {
            to.addValidators([dateWithTimeValidators]);
        }

        if (from && from.value && to && to.value) {
            const fromIsEarlierThanToValidator = {
                name: "dateWithTimeFromIsEarlierThanDateWithTimeTo",
                fn: () => this.dateWithTimeFromIsEarlierThanDateWithTimeTo(from.value, to.value),
                errorMessage: () => i18n.t("errorMessages.dateFromIsEarlierThanDateTo")
            };

            from.addValidators([fromIsEarlierThanToValidator]);
        }
    }

    public static addValidatorsToDateTimePickerForOrdersAndTickets(from: ValidatedValue<Date | null>, to: ValidatedValue<Date | null>, arrivalDepartureDate: ValidatedValue<Date | null>) {
        from.removeValidators();
        from.clearValidation();

        to.removeValidators();
        to.clearValidation();

        arrivalDepartureDate.removeValidators();
        arrivalDepartureDate.clearValidation();

        const dateWithTimeValidators = {
            name: "correctDateWithTime",
            fn: this.isCorrectDateWithTime,
            errorMessage: () => i18n.t("reports.errors.correctDateWithTime")
        };

        if (from && from.value)  {
            from.addValidators([dateWithTimeValidators]);
        }

        if (to && to.value)  {
            to.addValidators([dateWithTimeValidators]);
        }

        if (arrivalDepartureDate && arrivalDepartureDate.value) {
            arrivalDepartureDate.addValidators([dateWithTimeValidators]);
        }

        if (from && from.value && to && to.value) {
            const fromIsEarlierThanToValidator = {
                name: "dateWithTimeFromIsEarlierThanDateWithTimeTo",
                fn: () => this.dateWithTimeFromIsEarlierThanDateWithTimeTo(from.value, to.value),
                errorMessage: () => i18n.t("errorMessages.dateFromIsEarlierThanDateTo")
            };

            from.addValidators([fromIsEarlierThanToValidator]);
        }
    }


    public static isCorrectNumberWithCommaOrEmpty(value: string): boolean {
        if (!value) {
            return true;
        }

        const valid =  /^-?[0-9]*[,.]?[0-9]*$/;

        return valid.test(value);
    }

    public static atLeastOneFieldHasBeenCompleted(validatedValues: ValidatedValue<string>[]): boolean {
        if (!validatedValues) {
            return false;
        }
        let count = 0;

        validatedValues.map((element) => {
            if (element.value) count++;
        });

        return count > 0;
    }

    public static differenceBetweenDatesIsGreaterThanMonth(dateFrom: Date | null, dateTo: Date | null): boolean {
        if (!dateFrom || !dateTo) {
            return false;
        }
        let diff = 0;
        if (this.dateFromIsEarlierThanDateTo(dateFrom, dateTo)) {
            diff = dateTo.getTime() - dateFrom.getTime();
        } else {
            diff = dateFrom.getTime() - dateTo.getTime();
        }
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        return days > 31;
    }

    public static isOnlyPositiveNumberOrEmpty(value: string): boolean {
        if (!value) {
            return true;
        }

        const valid =  /^\+?(0|[0-9]\d*)$/;

        return valid.test(value) && Math.sign(parseInt(value, 10)) >= 0;
    }

    public static oneDateRequiredValidator(dateOne: Date | null, dateTwo: Date | null, dateThree?: Date | null) : boolean {
        return !!(dateOne || dateTwo || dateThree);
    }

    public static addOneDateRequiredValidator(dateOne: ValidatedValue<Date | null>, dateTwo: ValidatedValue<Date | null>, dateThree?: ValidatedValue<Date | null>) {
        const oneDateRequired = {
            name: "oneDateRequired",
            fn: () => Validator.oneDateRequiredValidator(dateOne.value, dateTwo.value, dateThree && dateThree.value),
            errorMessage: () => `${i18n.t("reports.listOfShipments.errors.oneDateRequired")}`
        };

        dateOne.addValidators([oneDateRequired]);
        dateTwo.addValidators([oneDateRequired]);
        dateThree && dateThree.addValidators([oneDateRequired]);
    }

    public static hasNoMoreThanTwoDecimalPlacesOrEmpty(value: string): boolean {
        if (!value) {
            return true;
        }

        const valid =  /^\d*(\.\d{0,2})?$/;

        return valid.test(value);
    }

    public static isMultipleOfNumber(value: number, number: number): boolean {
        if (!value || !number || value <= 0 || number <= 0) {
            return false;
        } else {
            return value % number === 0;
        }
    }

    public static numberGreaterThanZero(value: number): boolean {
        return value > 0;
    }
}
